<script setup>

import {computed, onMounted, reactive, ref} from "vue";
import {useAdminStore} from "@/store/admin";
import {useToast} from "vue-toast-notification";
import MainPreloader from "@/components/shared/MainPreloader.vue";
import EmptyCard from "@/components/shared/EmptyCard.vue";
import FormInput from "@/components/ui/FormInput.vue";
import ActionButton from "@/components/ui/ActionButton.vue";
import AdminModal from "@/components/AdminView/AdminModal.vue";

const adminStore = useAdminStore();
const toast = useToast();

const items = computed(() => adminStore.customPages);

const editing = ref(null);
const showForm = ref(false);
const form = reactive(defaultForm());

function defaultForm() {
    return {
        description: "",
        slug: "",
        content: "",
    };
}

function resetForm() {
    Object.assign(form, defaultForm());
}

function openCreate() {
    editing.value = {};
    showForm.value = true;
    resetForm();
}

function openEdit(item) {
    editing.value = item;
    showForm.value = true;
    Object.assign(form, {
        description: item.description || "",
        slug: item.slug || "",
        content: item.content || "",
    });
}

function cancelForm() {
    editing.value = null;
    showForm.value = false;
    resetForm();
}

async function saveHandler() {
    const payload = {
        description: form.description,
        slug: form.slug,
        content: form.content,
    };
    const isEdit = !!editing.value?.id;
    const ok = isEdit
        ? await adminStore.updateItem('custom-pages', editing.value.id, payload)
        : await adminStore.createItem('custom-pages', payload);
    if (adminStore.error) {
        toast.error(adminStore.error);
        return;
    }
    if (ok) {
        toast.success(isEdit ? "Страница обновлена" : "Страница создана");
        cancelForm();
        await adminStore.loadList('custom-pages');
    }
}

async function deleteHandler(item) {
    if (!confirm(`Удалить страницу "${item.description || item.slug || item.id}"?`)) return;
    const ok = await adminStore.deleteItem('custom-pages', item.id);
    if (adminStore.error) {
        toast.error(adminStore.error);
        return;
    }
    if (ok) {
        toast.success("Страница удалена");
        await adminStore.loadList('custom-pages');
    }
}

onMounted(() => {
    adminStore.loadList('custom-pages');
});

</script>

<template>
  <div class="adminCrud">
    <MainPreloader
        v-if="adminStore.loading"
        position="fixed"
        :size="80"
        :stroke="3"
    />

    <div class="adminCrud__toolbar">
      <div class="adminCrud__toolbarTitle">Страницы</div>
      <ActionButton style="background-color: #6FC062; color: #212226;" @buttonClick="openCreate">
        <template #caption>Добавить страницу</template>
      </ActionButton>
    </div>

    <AdminModal :visible="showForm" :title="editing?.id ? 'Редактирование страницы' : 'Новая страница'" @close="cancelForm">
      <div class="adminCrud__formGrid">
        <div class="adminCrud__field">
          <label class="adminCrud__label">Описание</label>
          <form-input v-model="form.description"/>
        </div>
        <div class="adminCrud__field">
          <label class="adminCrud__label">Slug</label>
          <form-input v-model="form.slug"/>
        </div>
        <div class="adminCrud__field adminCrud__field--wide adminCrud__field--content">
          <label class="adminCrud__label">Контент</label>
          <form-input v-model="form.content" rows="20"/>
        </div>
      </div>
      <template #footer>
        <ActionButton @buttonClick="cancelForm">
          <template #caption>Отмена</template>
        </ActionButton>
        <ActionButton style="background-color: #6FC062; color: #212226;" @buttonClick="saveHandler">
          <template #caption>Сохранить</template>
        </ActionButton>
      </template>
    </AdminModal>

    <EmptyCard v-if="!items.length && !adminStore.loading"/>

    <table v-else-if="items.length" class="adminTable">
      <thead>
      <tr>
        <th>Slug</th>
        <th>Описание</th>
        <th>Контент</th>
        <th>Действия</th>
      </tr>
      </thead>
      <tbody>
      <tr v-for="item in items" :key="item.id">
        <td>{{ item.slug || '—' }}</td>
        <td>{{ item.description || '—' }}</td>
        <td class="adminCrud__cellContent">{{ item.content || '—' }}</td>
        <td class="adminCrud__actions">
          <button class="adminCrud__actionButton" title="Изменить" @click="openEdit(item)">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
          <button class="adminCrud__actionButton adminCrud__actionButton--danger" title="Удалить" @click="deleteHandler(item)">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M3 6h18" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M10 11v6M14 11v6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
        </td>
      </tr>
      </tbody>
    </table>
  </div>
</template>

<style scoped lang="scss">
.adminCrud {
  display: flex;
  flex-direction: column;
  gap: 14px;

  &__toolbar {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  &__toolbarTitle {
    color: var(--cd-base-green);
    font-size: 16px;
    font-family: var(--cd-default-font);
  }

  &__formGrid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;

    @include respond-to('md') {
      grid-template-columns: 1fr;
    }
  }

  &__field {
    display: flex;
    flex-direction: column;
    gap: 6px;

    &--wide {
      grid-column: 1 / -1;
    }

    &--content {
      :deep(.formInput) {
        min-height: 30vh;
        height: auto;
        resize: vertical;
        width: 100%;
      }
    }
  }

  &__label {
    color: var(--cd-gray-alt);
    font-size: 13px;
  }

  &__cellContent {
    max-width: 320px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__actions {
    vertical-align: middle;
    white-space: nowrap;
    font-size: 0;

    .adminCrud__actionButton + .adminCrud__actionButton {
      margin-left: 8px;
    }
  }

  &__actionButton {
    background: transparent;
    border: 1px solid var(--cd-base-green);
    color: var(--cd-base-green);
    border-radius: 8px;
    padding: 6px;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    transition: all 0.3s;

    &:hover {
      background: var(--cd-base-green);
      color: #ffffff;
    }

    &--danger {
      border-color: var(--cd-danger);
      color: var(--cd-danger);

      &:hover {
        background: var(--cd-danger);
        color: #ffffff;
      }
    }
  }
}

.adminTable {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0 8px;
  text-align: left;

  th {
    color: var(--cd-text-muted);
    font-size: 12px;
    font-weight: 400;
    padding: 0 16px 4px;
  }

  td {
    background: var(--cd-card-bg);
    padding: 12px 16px;
    font-size: 14px;
    color: var(--cd-text);

    &:first-child {
      border-radius: 12px 0 0 12px;
    }

    &:last-child {
      border-radius: 0 12px 12px 0;
    }
  }
}
</style>